import { useCallback, useEffect, useRef } from 'react';

/**
 * Wraps an async function that accepts an `AbortSignal` and returns a stable
 * trigger function. Each call to the trigger automatically aborts any
 * in-flight request before starting a new one, and any pending request is
 * aborted on unmount. Errors from intentionally aborted requests are silently
 * swallowed; all other errors are re-thrown.
 *
 * Useful for multi-step fetch orchestrations (e.g. sequential lazy queries)
 * where you need both reactive re-fetching (via useEffect) and imperative
 * re-fetching (e.g. after a mutation) through the same function.
 *
 * @example
 * const fetchData = useCallback(async (signal: AbortSignal) => {
 *   const result = await getSomething({ context: { fetchOptions: { signal } } });
 *   if (signal.aborted) return;
 *   setData(result);
 * }, [getSomething]);
 *
 * const refetch = useAbortableTrigger(fetchData);
 *
 * // Reactive: re-runs when fetchData identity changes
 * useEffect(() => { refetch(); }, [refetch]);
 *
 * // Imperative: call after a mutation to refresh data
 * await someMutation();
 * await refetch();
 *
 * @remarks
 * The returned trigger resolves to `void`. Non-abort errors are re-thrown, so
 * if a caller `await`s the trigger it should wrap the call in `try/catch` to
 * avoid unhandled promise rejections. Fire-and-forget usage (e.g. calling from
 * `useEffect` without awaiting) is safe because React does not observe the
 * rejected promise.
 */
export const useAbortableTrigger = (
  fn: (signal: AbortSignal) => Promise<void>,
): (() => Promise<void>) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const trigger = useCallback(async (): Promise<void> => {
    abortControllerRef.current?.abort();
    const ac = new AbortController();
    abortControllerRef.current = ac;

    try {
      await fn(ac.signal);
    } catch (error) {
      if (!ac.signal.aborted) {
        throw error;
      }
    }
  }, [fn]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  return trigger;
};
